
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/context/LanguageContext";
import { AlertCircle, CheckCircle } from "lucide-react";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  // Get parameters from URL - Supabase redirects with these parameters
  const accessToken = searchParams.get("access_token");
  const refreshToken = searchParams.get("refresh_token");
  const tokenHash = searchParams.get("token_hash");
  const type = searchParams.get("type");
  const errorParam = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  useEffect(() => {
    const verifyResetToken = async () => {
      console.log("Reset password params:", { 
        accessToken: accessToken ? "present" : "missing",
        refreshToken: refreshToken ? "present" : "missing", 
        tokenHash: tokenHash ? "present" : "missing",
        type,
        error: errorParam,
        errorDescription
      });
      
      // Check if there's an error in the URL parameters
      if (errorParam) {
        console.error("Reset password error from URL:", errorParam, errorDescription);
        setIsTokenValid(false);
        return;
      }
      
      // Handle session-based reset (when user clicks link from email)
      if (accessToken && refreshToken) {
        try {
          console.log("Setting session with tokens from URL");
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error("Session set error:", error);
            setIsTokenValid(false);
            return;
          }

          if (data.user) {
            console.log("Session set successfully for user:", data.user.id);
            setIsTokenValid(true);
          } else {
            console.log("No user found in session");
            setIsTokenValid(false);
          }
        } catch (err) {
          console.error("Error setting session:", err);
          setIsTokenValid(false);
        }
        return;
      }

      // Handle OTP-based reset (if using token_hash)
      if (tokenHash && type === 'recovery') {
        try {
          console.log("Verifying OTP with token hash");
          const { data, error } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash: tokenHash,
          });

          if (error) {
            console.error("OTP verification error:", error);
            setIsTokenValid(false);
            return;
          }

          if (data.user) {
            console.log("OTP verified successfully for user:", data.user.id);
            setIsTokenValid(true);
          } else {
            console.log("No user found in OTP verification");
            setIsTokenValid(false);
          }
        } catch (err) {
          console.error("Error verifying OTP:", err);
          setIsTokenValid(false);
        }
        return;
      }

      // If no valid parameters found, check current session
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          console.log("Valid session found for user:", session.user.id);
          setIsTokenValid(true);
        } else {
          console.log("No valid session found");
          setIsTokenValid(false);
        }
      } catch (err) {
        console.error("Error checking session:", err);
        setIsTokenValid(false);
      }
    };

    verifyResetToken();
  }, [accessToken, refreshToken, tokenHash, type, errorParam, errorDescription]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Password validation
    if (password.length < 6) {
      setPasswordError(t('auth.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError(t('auth.passwordsDontMatch'));
      return;
    }

    setPasswordError(null);
    setIsLoading(true);

    try {
      console.log("Updating user password");
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        console.error("Password update error:", error);
        throw error;
      }

      console.log("Password updated successfully");
      toast({
        title: t('auth.passwordResetSuccess'),
        description: t('auth.passwordResetSuccessDesc'),
      });

      // Sign out user and redirect to login page
      await supabase.auth.signOut();
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        variant: "destructive",
        title: t('auth.passwordResetFailed'),
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show different content based on token validity
  if (isTokenValid === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-lg text-center">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('auth.invalidOrExpiredResetLink')}
            </AlertDescription>
          </Alert>
          <p className="mt-4 text-gray-600">
            {t('auth.requestNewResetLinkDesc')}
          </p>
          <Button 
            onClick={() => navigate("/forgot-password")} 
            className="mt-4"
          >
            {t('auth.forgotPassword')}
          </Button>
        </div>
      </div>
    );
  }

  if (isTokenValid === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-lg text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p>{t('auth.verifyingResetLink')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-lg">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight">{t('auth.resetYourPassword')}</h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.enterNewPassword')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="password">{t('auth.newPassword')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            {passwordError && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? t('auth.updating') : t('auth.resetPassword')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
